import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatUserName = (user) => {
  if (!user) return "—";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return user.userName || name || user.uuid || "—";
};

const StakingCommissionHistory = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalCommission: 0,
    byLevel: { level1: 0, level2: 0, level3: 0, level4: 0, level5: 0 },
  });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [searchQuery, setSearchQuery] = useState("");

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((item) => {
      const user = item?.from_user;
      const currency = item?.currency?.short_name || item?.plan?.currency || "";
      const haystack = [
        formatUserName(user),
        user?.uuid,
        user?.userName,
        currency,
        String(item?.level ?? ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, searchQuery]);

  const loadCommissions = async (targetPage) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getStakingReferralCommissions(targetPage, limit);
      if (result?.success) {
        const data = result?.data || {};
        setRows(Array.isArray(data.items) ? data.items : []);
        setSummary({
          totalCommission: toNum(data.summary?.totalCommission),
          byLevel: {
            level1: toNum(data.summary?.byLevel?.level1),
            level2: toNum(data.summary?.byLevel?.level2),
            level3: toNum(data.summary?.byLevel?.level3),
            level4: toNum(data.summary?.byLevel?.level4),
            level5: toNum(data.summary?.byLevel?.level5),
          },
        });
        setPagination({
          total: Number(data.pagination?.total) || 0,
          totalPages: Math.max(1, Number(data.pagination?.totalPages) || 1),
        });
        setPage(Number(data.pagination?.page) || targetPage);
      } else {
        setRows([]);
        setSummary({
          totalCommission: 0,
          byLevel: { level1: 0, level2: 0, level3: 0, level4: 0, level5: 0 },
        });
        setPagination({ total: 0, totalPages: 1 });
      }
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  useEffect(() => {
    loadCommissions(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { total, totalPages } = pagination;
  const currencySymbol = rows[0]?.currency?.short_name || rows[0]?.plan?.currency || "USDT";

  const renderPagination = () =>
    rows.length > 0 ? (
      <div className="hVPalX gap-2">
        <span>
          {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
        </span>
        <div className="sc-eAKtBH gVtWSU">
          <button
            type="button"
            aria-label="First Page"
            className="sc-gjLLEI kuPCgf"
            onClick={() => setPage(1)}
            disabled={page <= 1}
          >
            <i className="ri-skip-back-fill text-white" />
          </button>
          <button
            type="button"
            aria-label="Previous Page"
            className="sc-gjLLEI kuPCgf"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <i className="ri-arrow-left-s-line text-white" />
          </button>
          <button
            type="button"
            aria-label="Next Page"
            className="sc-gjLLEI kuPCgf"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            <i className="ri-arrow-right-s-line text-white" />
          </button>
          <button
            type="button"
            aria-label="Last Page"
            className="sc-gjLLEI kuPCgf"
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages}
          >
            <i className="ri-skip-forward-fill text-white" />
          </button>
        </div>
      </div>
    ) : null;

  const renderEmptyRow = () => (
    <tr className="no-data-row">
      <td colSpan="9">
        <div className="no-data-wrapper">
          <div className="no_data_vector">
            <img src="/images/no_data_vector.svg" className="img-fluid dark_img" width="96" height="96" alt="No data" />
            <img src="/images/no_data_vector_light.png" className="img-fluid light_img" alt="No data" />
          </div>
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <Helmet>
        <title>Staking Commission | Zillion Exchange</title>
      </Helmet>

      <div className="dashboard_right">
        <div className="dashboard_listing_section Overview_mid">
          <div className="listing_left_outer full_width transaction_history_t desktop_view2">
            <div className="market_section spotorderhist">
              <div className="top_heading">
                <h4>Staking Commission</h4>
                <div className="coin_right">
                  <div className="searchBar custom-tabs">
                    <i className="ri-search-2-line" />
                    <input
                      type="search"
                      className="custom_search"
                      placeholder="Search user or currency"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Total Commission</div>
                      <div className="fw-semibold">
                        {summary.totalCommission} {currencySymbol}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Level 1</div>
                      <div className="fw-semibold text-success">
                        {summary.byLevel.level1} {currencySymbol}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Level 2</div>
                      <div className="fw-semibold">
                        {summary.byLevel.level2} {currencySymbol}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Level 3</div>
                      <div className="fw-semibold">
                        {summary.byLevel.level3} {currencySymbol}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Level 4</div>
                      <div className="fw-semibold">
                        {summary.byLevel.level4} {currencySymbol}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="card border h-100">
                    <div className="card-body p-3">
                      <div className="small text-muted">Level 5</div>
                      <div className="fw-semibold">
                        {summary.byLevel.level5} {currencySymbol}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashboard_summary">
                <table>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Level</th>
                      <th>From User</th>
                      <th>Currency</th>
                      <th>Payout Amount</th>
                      <th>Commission %</th>
                      <th>Commission Amount</th>
                      <th>Plan Duration</th>
                      <th className="right_td">Credited At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length > 0
                      ? filteredRows.map((item, index) => {
                          const sym = item?.currency?.short_name || item?.plan?.currency || "USDT";
                          return (
                            <tr key={item?.id ?? index}>
                              <td>{(page - 1) * limit + index + 1}</td>
                              <td>Level {item?.level ?? "—"}</td>
                              <td>{formatUserName(item?.from_user)}</td>
                              <td>{sym}</td>
                              <td>
                                {toNum(item?.payout_amount)} {sym}
                              </td>
                              <td>{toNum(item?.commission_percent)}%</td>
                              <td className="text-success">
                                +{toNum(item?.commission_amount)} {sym}
                              </td>
                              <td>
                                {item?.plan?.duration_days != null
                                  ? `${item.plan.duration_days} days`
                                  : "—"}
                              </td>
                              <td className="right_td">
                                {item?.credited_at
                                  ? moment(item.credited_at).format("YYYY-MM-DD HH:mm")
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })
                      : renderEmptyRow()}
                  </tbody>
                </table>
                {renderPagination()}
              </div>
            </div>
          </div>

          <div className="order_history_mobile_view">
            <div className="coin_right d-flex flex-row justify-content-between align-items-center p-0">
              <h5>Staking Commission</h5>
              <div className="d-flex flex-row justify-content-end align-items-end mb-3">
                <div className="searchBar custom-tabs">
                  <i className="ri-search-2-line" />
                  <input
                    type="search"
                    className="custom_search"
                    placeholder="Search user or currency"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="row g-2 mb-3 px-2">
              <div className="col-6">
                <div className="card border h-100">
                  <div className="card-body p-2">
                    <div className="small text-muted">Total</div>
                    <div className="fw-semibold small">
                      {summary.totalCommission} {currencySymbol}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="card border h-100">
                  <div className="card-body p-2">
                    <div className="small text-muted">L1 / L2 / L3 / L4 / L5</div>
                    <div className="fw-semibold small">
                      {summary.byLevel.level1} / {summary.byLevel.level2} / {summary.byLevel.level3} /{" "}
                      {summary.byLevel.level4} / {summary.byLevel.level5}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex">
              {filteredRows.length > 0 ? (
                filteredRows.map((item, index) => {
                  const sym = item?.currency?.short_name || item?.plan?.currency || "USDT";
                  return (
                    <div key={item?.id ?? index} className="order_datalist">
                      <ul className="listdata">
                        <li>
                          <span className="date">Level</span>
                          <span className="date_light">Level {item?.level ?? "—"}</span>
                        </li>
                        <li>
                          <span>From User</span>
                          <span>{formatUserName(item?.from_user)}</span>
                        </li>
                        <li>
                          <span>Currency</span>
                          <span>{sym}</span>
                        </li>
                        <li>
                          <span>Payout Amount</span>
                          <span>
                            {toNum(item?.payout_amount)} {sym}
                          </span>
                        </li>
                        <li>
                          <span>Commission</span>
                          <span className="text-success">
                            +{toNum(item?.commission_amount)} {sym} ({toNum(item?.commission_percent)}%)
                          </span>
                        </li>
                        <li>
                          <span>Plan Duration</span>
                          <span>
                            {item?.plan?.duration_days != null
                              ? `${item.plan.duration_days} days`
                              : "—"}
                          </span>
                        </li>
                        <li>
                          <span>Credited At</span>
                          <span>
                            {item?.credited_at
                              ? moment(item.credited_at).format("YYYY-MM-DD HH:mm")
                              : "—"}
                          </span>
                        </li>
                      </ul>
                    </div>
                  );
                })
              ) : (
                <div className="no-data-wrapper w-100">
                  <div className="no_data_vector">
                    <img src="/images/no_data_vector.svg" className="img-fluid dark_img" width="96" height="96" alt="No data" />
                    <img src="/images/no_data_vector_light.png" className="img-fluid light_img" alt="No data" />
                  </div>
                </div>
              )}
            </div>
            {renderPagination()}
          </div>
        </div>
      </div>
    </>
  );
};

export default StakingCommissionHistory;
